import { ThreeDSecureVerifyPayload } from 'braintree-web';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { authorizePayment } from '../../../services/braintreeService';
import { Additional3dsInfo } from '../../../types';
import { useErrorContext } from '../../ErrorContextProvider';

export type BraintreeAuthorizationResult = {
  nonce: string;
  liabilityShifted: boolean;
  liabilityShiftPossible: boolean;
  status: string;
  deviceData: string;
};

export type BraintreeAuthorizerProps = {
  token: string;
  bin: string;
  nonce: string;
  amount: number;
  currency: string;
  additional3dsInfo?: Additional3dsInfo;
};

// Override since typescript types for braintree are not inline with actual implementation ...
export type ThreeDSecureVerifyPayloadOverride = Omit<
  ThreeDSecureVerifyPayload,
  'details' | 'threeDSecureInfo'
> & {
  details: {
    cardType: string;
    lastTwo: string;
    lastFour: string;
    expirationYear: string;
    expirationMonth: string;
    cardholderName?: string;
    bin: string;
  };
  threeDSecureInfo: {
    liabilityShiftPossible: boolean;
    liabilityShifted: boolean;
    cavv: string;
    xid: string;
    dsTransactionId: string;
    threeDSecureVersion: string;
    eciFlag: string;
    threeDSecureAuthenticationId: string;
    status: string;
  };
};

const BraintreeAuthorizer: React.FC<BraintreeAuthorizerProps> = ({
  token,
  nonce,
  bin,
  amount,
  additional3dsInfo,
}) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  const handleAuthorize = useCallback(async () => {
    const result = await authorizePayment({
      t,
      token,
      bin,
      nonce,
      amount,
      additional3dsInfo,
    });

    if (!result.success) {
      setError({ message: result.errorMessage, retryable: result.retryable });
      throw new Error(result.errorMessage);
    }
    return result;
  }, [t, setError, token, bin, nonce, amount, additional3dsInfo]);

  useEffect(() => {
    (window as any).Payment = {
      ...((window as any).Payment || {}),
      braintreeAuthorize: handleAuthorize,
    };
  }, [handleAuthorize]);

  return null;
};

export default BraintreeAuthorizer;
